import { DocumentReviewPreview } from './DocumentReviewPreview';
import { documentCompletionIssues } from './pdfState';
import type { ComponentChildren } from 'preact';
import { RichText } from '../components/RichText';
import { useEffect, useMemo, useState } from 'preact/hooks';
import type { BuiltinAnswers, CustomAnswer, PublicEvent, PdfRevisionProgress } from '../../shared/types';
import { activeQuestionFields, questionSections } from '../../shared/questionRouting';
import { answerToText, isInputField } from '../../shared/answers';
import { PdfPageViewer } from '../components/pdf/PdfPageViewer';
import { PdfDownloadButton } from '../components/pdf/PdfDownloadButton';
import type { PdfGenerationResult } from '../../shared/pdf/generate';
import { AnswerSummary } from './Summary';
import { STAGED_PROTOCOL } from '../../shared/stagedSubmission';

export const SUBMISSION_UNAVAILABLE = 'Registration can’t be sent from this page right now. Your progress is saved in this tab. Refresh this page and try again, or try again later.';

interface Props {
  progress?: PdfRevisionProgress;
  /** The registration's date, shown in date-submitted boxes. */
  submittedOn: string;
  ev: PublicEvent; b: BuiltinAnswers; preview: boolean; onEdit: (step: number) => void;
  /** Edit the extra questions, starting at this section (null for the questions before any section). */ onEditQuestions: (sectionId: string | null) => void;
  /** The signed permission form, once it's prepared. */ signed: PdfGenerationResult | null; custom: Record<string, CustomAnswer>; fileName: string;
  /** Goes back a step, from the last row beside Submit registration. */ onBack: () => void;
  /** Opens a form's own step, to finish what it still needs. */ onEditDocument: (documentId: string) => void;
  /** Notices from the registration shown just above that row, such as a form that couldn't be prepared. */ children?: ComponentChildren;
  acknowledged: boolean; onAcknowledge: (v: boolean) => void; submitting: boolean; preparing: boolean; onSubmit: () => void; error: string | null;
}

export function ReviewStep(p: Props) {
  const pdf = p.signed?.bytes ?? null;
  const url = useMemo(() => (pdf ? URL.createObjectURL(new Blob([pdf as BlobPart], { type: 'application/pdf' })) : null), [pdf]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const [ready, setReady] = useState<Record<string, boolean>>({});
  const documentReady = (id: string, value: boolean) => setReady(old => old[id] === value ? old : { ...old, [id]: value });
  const blocked = p.submitting || !!p.progress?.submissionAttemptNeedsReconciliation;
  const formsUnavailable = !!p.ev.documentForms?.length && !p.ev.pdfSubmissionEnabled;
  // A page loaded before the current submission protocol, or while new submissions are paused.
  const protocolUnavailable = !p.ev.submissionProtocols?.includes(STAGED_PROTOCOL);
  const unavailable = formsUnavailable || protocolUnavailable;
  const documentsReady = (p.ev.documentForms ?? []).every(d => ready[d.id]);
  // The answers to the organizer's questions, a card for each section, leaving out what the family's answers skipped.
  const active = new Set(activeQuestionFields(p.ev.fields, p.custom).filter(isInputField).map(f => f.id));
  const questionGroups = questionSections(p.ev.fields).map(section => {
    const heading = section.fields[0]?.type === 'section' ? section.fields[0] : undefined;
    return { id: section.id, heading, fields: section.fields.filter(f => active.has(f.id)) };
  }).filter(group => group.fields.length);

  return (
    <div class="stack">
      <h2 tabIndex={-1}>Review and submit</h2>
      {/* The step families most often miss, set apart in the site's notice box. */}
      <p class="notice">Your registration isn’t complete until you scroll to the bottom and click <strong>Submit registration</strong>.</p>
      <p>Check your details before submitting them. If you change any answers, you’ll need to sign again.</p>
      <AnswerSummary ev={p.ev} b={p.b} onEdit={p.onEdit} disabled={blocked} />
      {/* The answers to the organizer's questions sit with the other answers, above the signed forms: a card for each
        section, titled by the section, or "Questions" when it has no title. */}
      {questionGroups.map(group => {
        const title = group.heading?.label.trim() ? <RichText text={group.heading.label} format={group.heading.labelFormat} /> : 'Questions';
        const name = group.heading?.label.trim() || 'Questions';
        return <section class="reg-summary-card" key={group.id ?? 'questions'}>
          <div class="row between"><h3>{title}</h3><button disabled={blocked} class="link" onClick={() => p.onEditQuestions(group.id)} aria-label={`Edit ${name}`}>Edit</button></div>
          <dl class="answers">
            {group.fields.map(f => <><dt><RichText text={f.label} format={f.labelFormat} /></dt><dd>{answerToText(f, p.custom[f.id]) || 'None provided'}</dd></>)}
          </dl>
        </section>;
      })}
      {/* The signed forms in the order they were signed: the additional forms, then the Permission and Medical Release Form. */}
      {(p.ev.documentForms ?? []).map(document => <section class="card stack" key={document.id}>
        <strong>{document.title}</strong>
        {p.progress?.documents[document.id] && <DocumentReviewPreview event={p.ev} document={document} builtin={p.b} custom={p.custom} completion={p.progress.documents[document.id].completion} preview={p.preview} onReady={documentReady} submittedOn={p.submittedOn}
          issues={documentCompletionIssues(p.ev, document, { builtin: p.b, custom: p.custom, step: 7, pdfProgress: p.progress, standardSignedOn: p.submittedOn })} onEdit={blocked ? undefined : () => p.onEditDocument(document.id)} />}
      </section>)}
      {/* The signed permission form, always open, laid out as the additional forms are: in the same viewer, with a
        download button at the start of its controls and zoom buttons at the end. */}
      <section class="card stack">
        <strong>Permission and Medical Release Form</strong>
        {p.preparing && <p class="muted small" role="status">Preparing your signed form…</p>}
        {p.signed ? <PdfPageViewer eventId={p.ev.eventId ?? ''} generated={p.signed} overlay={() => null}
          leading={url && <PdfDownloadButton url={url} fileName={p.fileName} title="Permission and Medical Release Form" />} />
          : p.preview && !p.preparing && <p class="muted">Sign the form to preview the signed PDF.</p>}
      </section>
      <label class="inline ack">
        <input type="checkbox" disabled={blocked} checked={p.acknowledged} onChange={() => p.onAcknowledge(!p.acknowledged)} />
        <span>I wish to acknowledge my electronic signatures and submit this registration.</span>
      </label>
      {formsUnavailable && !p.preview && <p class="reg-note">Submission for this event’s additional forms is not available yet. Keep this tab open or return later; your progress is saved here.</p>}
      {!formsUnavailable && protocolUnavailable && !p.preview && <p class="reg-note">{SUBMISSION_UNAVAILABLE}</p>}
      {p.preview && <p class="reg-note">This is a preview. No registration will be sent.</p>}
      {p.error && <p role="alert" class="alert">{p.error}</p>}
      {p.children}
      {/* Back and Submit registration share the last row, Submit at the right, with no line above them. */}
      <div class="row between nav review-nav">
        <button class="secondary" onClick={p.onBack} disabled={p.submitting}>Back</button>
        <button class="primary" disabled={p.preview || blocked || unavailable || !documentsReady || !p.acknowledged || p.submitting || !pdf} onClick={p.onSubmit}>{p.preview ? 'Submission disabled in preview' : p.submitting ? 'Submitting…' : 'Submit registration'}</button>
      </div>
    </div>
  );
}
